import { createServerFn } from '@tanstack/react-start'
import { z } from 'zod'
import { Query } from 'node-appwrite'
import { db } from '../lib/db'
import { authMiddleware } from './auth'

// Generate a random short code
function generateShortCode(length = 6): string {
  const chars = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
  let result = ''
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return result
}

// Validate URL
function isValidUrl(url: string): boolean {
  try {
    new URL(url)
    return true
  } catch {
    return false
  }
}

// Parse user agent for device info
function parseUserAgent(userAgent: string | null): {
  device: string
  browser: string
  os: string
} {
  if (!userAgent) {
    return { device: 'Unknown', browser: 'Unknown', os: 'Unknown' }
  }

  // Device detection
  let device = 'Desktop'
  if (/mobile/i.test(userAgent)) device = 'Mobile'
  else if (/tablet|ipad/i.test(userAgent)) device = 'Tablet'

  // Browser detection
  let browser = 'Unknown'
  if (/chrome/i.test(userAgent) && !/edge|edg/i.test(userAgent))
    browser = 'Chrome'
  else if (/firefox/i.test(userAgent)) browser = 'Firefox'
  else if (/safari/i.test(userAgent) && !/chrome/i.test(userAgent))
    browser = 'Safari'
  else if (/edge|edg/i.test(userAgent)) browser = 'Edge'
  else if (/opera|opr/i.test(userAgent)) browser = 'Opera'

  // OS detection
  let os = 'Unknown'
  if (/windows/i.test(userAgent)) os = 'Windows'
  else if (/macintosh|mac os/i.test(userAgent)) os = 'macOS'
  else if (/linux/i.test(userAgent) && !/android/i.test(userAgent)) os = 'Linux'
  else if (/android/i.test(userAgent)) os = 'Android'
  else if (/iphone|ipad|ipod/i.test(userAgent)) os = 'iOS'

  return { device, browser, os }
}

// Create a new short link
const createLinkSchema = z.object({
  originalUrl: z.string().min(1, 'URL is required'),
  customCode: z.string().max(20).optional(),
  title: z.string().max(100).optional(),
  description: z.string().max(500).optional(),
  password: z.string().max(100).optional(),
  expiresAt: z.number().optional(), // Unix timestamp
  tags: z.array(z.string()).optional(),
  // UTM parameters
  utmSource: z.string().max(100).optional(),
  utmMedium: z.string().max(100).optional(),
  utmCampaign: z.string().max(100).optional(),
  utmTerm: z.string().max(100).optional(),
  utmContent: z.string().max(100).optional(),
  // Device-specific redirects
  androidRedirect: z.string().optional(),
  iosRedirect: z.string().optional(),
  // Custom OG tags
  customOgTitle: z.string().max(100).optional(),
  customOgDescription: z.string().max(300).optional(),
  customOgImage: z.string().optional(),
})

export const createLinkFn = createServerFn({ method: 'POST' })
  .inputValidator(createLinkSchema)
  .handler(async ({ data }) => {
    const { currentUser } = await authMiddleware()

    // Allow anonymous users to create links
    const userId = currentUser?.$id ?? 'anonymous'

    let url = data.originalUrl.trim()

    // Add https:// if no protocol specified
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url
    }

    if (!isValidUrl(url)) {
      throw new Error('Invalid URL format')
    }

    // Validate device-specific URLs
    if (data.androidRedirect) {
      let androidUrl = data.androidRedirect.trim()
      if (
        !androidUrl.startsWith('http://') &&
        !androidUrl.startsWith('https://')
      ) {
        androidUrl = 'https://' + androidUrl
      }
      if (!isValidUrl(androidUrl)) {
        throw new Error('Invalid Android redirect URL')
      }
      data.androidRedirect = androidUrl
    }

    if (data.iosRedirect) {
      let iosUrl = data.iosRedirect.trim()
      if (!iosUrl.startsWith('http://') && !iosUrl.startsWith('https://')) {
        iosUrl = 'https://' + iosUrl
      }
      if (!isValidUrl(iosUrl)) {
        throw new Error('Invalid iOS redirect URL')
      }
      data.iosRedirect = iosUrl
    }

    // Generate or use custom short code
    let shortCode = data.customCode?.trim() || generateShortCode()

    // Check if custom code already exists
    if (data.customCode) {
      const existing = await db.links.list([
        Query.equal('shortCode', [shortCode]),
      ])
      if (existing.total > 0) {
        throw new Error('This short code is already taken')
      }
    } else {
      // Ensure generated code is unique
      let attempts = 0
      while (attempts < 5) {
        const existing = await db.links.list([
          Query.equal('shortCode', [shortCode]),
        ])
        if (existing.total === 0) break
        shortCode = generateShortCode()
        attempts++
      }
    }

    const link = await db.links.create({
      createdBy: userId,
      shortCode,
      originalUrl: url,
      title: data.title?.trim() || null,
      description: data.description?.trim() || null,
      clicks: 0,
      isActive: true,
      password: data.password || null,
      tags: data.tags || null,
      utmSource: data.utmSource || null,
      utmMedium: data.utmMedium || null,
      utmCampaign: data.utmCampaign || null,
      utmTerm: data.utmTerm || null,
      utmContent: data.utmContent || null,
      androidRedirect: data.androidRedirect || null,
      iosRedirect: data.iosRedirect || null,
      customOgTitle: data.customOgTitle || null,
      customOgDescription: data.customOgDescription || null,
      customOgImage: data.customOgImage || null,
    })

    return { link }
  })

// Get all links for current user
export const listLinksFn = createServerFn({ method: 'GET' }).handler(
  async () => {
    const { currentUser } = await authMiddleware()

    if (!currentUser) {
      return { links: [] }
    }

    const result = await db.links.list([
      Query.equal('createdBy', [currentUser.$id]),
      Query.orderDesc('$createdAt'),
      Query.limit(100),
    ])

    return { links: result.rows }
  },
)

// Get a single link by ID
const getLinkSchema = z.object({
  id: z.string(),
})

export const getLinkFn = createServerFn({ method: 'GET' })
  .inputValidator(getLinkSchema)
  .handler(async ({ data }) => {
    const { currentUser } = await authMiddleware()

    if (!currentUser) {
      throw new Error('Unauthorized')
    }

    const link = await db.links.get(data.id)

    if (!link || link.createdBy !== currentUser.$id) {
      throw new Error('Link not found')
    }

    return { link }
  })

// Get link by short code (for redirects - public)
const getLinkByCodeSchema = z.object({
  code: z.string(),
  password: z.string().optional(),
  userAgent: z.string().optional(),
  referer: z.string().optional(),
})

export const getLinkByCodeFn = createServerFn({ method: 'GET' })
  .inputValidator(getLinkByCodeSchema)
  .handler(async ({ data }) => {
    const result = await db.links.list([
      Query.equal('shortCode', [data.code]),
      Query.equal('isActive', [true]),
      Query.limit(1),
    ])

    if (result.total === 0) {
      return { link: null, requiresPassword: false }
    }

    const link = result.rows[0]

    // Check if password protected
    if (link.password && link.password !== data.password) {
      return { link: null, requiresPassword: true }
    }

    return { link, requiresPassword: false }
  })

// Record click event with analytics
const recordClickSchema = z.object({
  linkId: z.string(),
  shortCode: z.string(),
  userAgent: z.string().optional(),
  referer: z.string().optional(),
  ip: z.string().optional(),
  utmSource: z.string().optional(),
  utmMedium: z.string().optional(),
  utmCampaign: z.string().optional(),
})

export const recordClickFn = createServerFn({ method: 'POST' })
  .inputValidator(recordClickSchema)
  .handler(async ({ data }) => {
    // Get link to find owner
    const link = await db.links.get(data.linkId)
    if (!link) {
      throw new Error('Link not found')
    }

    // Parse user agent
    const { device, browser, os } = parseUserAgent(data.userAgent || null)

    // Create click event
    await db.clickEvents.create({
      createdBy: link.createdBy,
      linkId: data.linkId,
      shortCode: data.shortCode,
      country: null, // Would need geo-IP service
      city: null,
      region: null,
      device,
      browser,
      os,
      referer: data.referer || null,
      ip: data.ip || null,
      userAgent: data.userAgent || null,
      utmSource: data.utmSource || null,
      utmMedium: data.utmMedium || null,
      utmCampaign: data.utmCampaign || null,
    })

    // Increment click count
    await db.links.update(data.linkId, {
      clicks: link.clicks + 1,
    })

    return { success: true }
  })

// Get analytics for a link
const getLinkAnalyticsSchema = z.object({
  linkId: z.string(),
  days: z.number().min(1).max(90).optional(),
})

export const getLinkAnalyticsFn = createServerFn({ method: 'GET' })
  .inputValidator(getLinkAnalyticsSchema)
  .handler(async ({ data }) => {
    const { currentUser } = await authMiddleware()

    if (!currentUser) {
      throw new Error('Unauthorized')
    }

    // Verify ownership
    const link = await db.links.get(data.linkId)
    if (!link || link.createdBy !== currentUser.$id) {
      throw new Error('Link not found')
    }

    const days = data.days || 30
    const startDate = new Date()
    startDate.setDate(startDate.getDate() - days)

    // Get click events
    const events = await db.clickEvents.list([
      Query.equal('linkId', [data.linkId]),
      Query.greaterThan('$createdAt', startDate.toISOString()),
      Query.orderDesc('$createdAt'),
      Query.limit(1000),
    ])

    // Aggregate data
    const clicksByDate: Record<string, number> = {}
    const clicksByDevice: Record<string, number> = {}
    const clicksByBrowser: Record<string, number> = {}
    const clicksByOS: Record<string, number> = {}
    const clicksByReferer: Record<string, number> = {}
    const clicksByCountry: Record<string, number> = {}

    events.rows.forEach((event) => {
      // By date
      const date = new Date(event.$createdAt).toISOString().split('T')[0]
      clicksByDate[date] = (clicksByDate[date] || 0) + 1

      // By device
      const device = event.device || 'Unknown'
      clicksByDevice[device] = (clicksByDevice[device] || 0) + 1

      // By browser
      const browser = event.browser || 'Unknown'
      clicksByBrowser[browser] = (clicksByBrowser[browser] || 0) + 1

      // By OS
      const os = event.os || 'Unknown'
      clicksByOS[os] = (clicksByOS[os] || 0) + 1

      // By referer
      const referer = event.referer || 'Direct'
      clicksByReferer[referer] = (clicksByReferer[referer] || 0) + 1

      // By country
      const country = event.country || 'Unknown'
      clicksByCountry[country] = (clicksByCountry[country] || 0) + 1
    })

    return {
      totalClicks: events.total,
      clicksByDate,
      clicksByDevice,
      clicksByBrowser,
      clicksByOS,
      clicksByReferer,
      clicksByCountry,
      recentClicks: events.rows.slice(0, 50),
    }
  })

// Get dashboard stats
export const getDashboardStatsFn = createServerFn({ method: 'GET' }).handler(
  async () => {
    const { currentUser } = await authMiddleware()

    if (!currentUser) {
      return {
        totalLinks: 0,
        totalClicks: 0,
        activeLinks: 0,
        clicksToday: 0,
        clicksThisWeek: 0,
        topLinks: [],
      }
    }

    // Get all links
    const links = await db.links.list([
      Query.equal('createdBy', [currentUser.$id]),
      Query.limit(100),
    ])

    const totalLinks = links.total
    const activeLinks = links.rows.filter((link) => link.isActive).length

    // Get clicks today and this week
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const weekAgo = new Date()
    weekAgo.setDate(weekAgo.getDate() - 7)

    const recentEvents = await db.clickEvents.list([
      Query.equal('createdBy', [currentUser.$id]),
      Query.greaterThan('$createdAt', weekAgo.toISOString()),
      Query.limit(1000),
    ])

    const clicksToday = recentEvents.rows.filter(
      (e) => new Date(e.$createdAt) >= today,
    ).length

    const clicksThisWeek = recentEvents.total

    // Get total clicks from the links table (sum of all link clicks)
    // This is the source of truth as it's updated atomically with each click
    const totalClicks = links.rows.reduce((sum, link) => sum + link.clicks, 0)

    // Top performing links
    const topLinks = [...links.rows]
      .sort((a, b) => b.clicks - a.clicks)
      .slice(0, 5)

    return {
      totalLinks,
      totalClicks,
      activeLinks,
      clicksToday,
      clicksThisWeek,
      topLinks,
    }
  },
)

// Update a link
const updateLinkSchema = z.object({
  id: z.string(),
  title: z.string().max(100).optional(),
  description: z.string().max(500).optional(),
  isActive: z.boolean().optional(),
  password: z.string().max(100).nullable().optional(),
  tags: z.array(z.string()).nullable().optional(),
  utmSource: z.string().max(100).nullable().optional(),
  utmMedium: z.string().max(100).nullable().optional(),
  utmCampaign: z.string().max(100).nullable().optional(),
  utmTerm: z.string().max(100).nullable().optional(),
  utmContent: z.string().max(100).nullable().optional(),
  androidRedirect: z.string().nullable().optional(),
  iosRedirect: z.string().nullable().optional(),
  customOgTitle: z.string().max(100).nullable().optional(),
  customOgDescription: z.string().max(300).nullable().optional(),
  customOgImage: z.string().nullable().optional(),
})

export const updateLinkFn = createServerFn({ method: 'POST' })
  .inputValidator(updateLinkSchema)
  .handler(async ({ data }) => {
    const { currentUser } = await authMiddleware()

    if (!currentUser) {
      throw new Error('Unauthorized')
    }

    const link = await db.links.get(data.id)

    if (!link || link.createdBy !== currentUser.$id) {
      throw new Error('Link not found')
    }

    const updateData: Record<string, unknown> = {}

    if (data.title !== undefined) {
      updateData.title = data.title?.trim() || null
    }
    if (data.description !== undefined) {
      updateData.description = data.description?.trim() || null
    }
    if (data.isActive !== undefined) {
      updateData.isActive = data.isActive
    }
    if (data.password !== undefined) {
      updateData.password = data.password || null
    }
    if (data.tags !== undefined) {
      updateData.tags = data.tags
    }
    if (data.utmSource !== undefined) {
      updateData.utmSource = data.utmSource || null
    }
    if (data.utmMedium !== undefined) {
      updateData.utmMedium = data.utmMedium || null
    }
    if (data.utmCampaign !== undefined) {
      updateData.utmCampaign = data.utmCampaign || null
    }
    if (data.utmTerm !== undefined) {
      updateData.utmTerm = data.utmTerm || null
    }
    if (data.utmContent !== undefined) {
      updateData.utmContent = data.utmContent || null
    }
    if (data.androidRedirect !== undefined) {
      updateData.androidRedirect = data.androidRedirect || null
    }
    if (data.iosRedirect !== undefined) {
      updateData.iosRedirect = data.iosRedirect || null
    }
    if (data.customOgTitle !== undefined) {
      updateData.customOgTitle = data.customOgTitle || null
    }
    if (data.customOgDescription !== undefined) {
      updateData.customOgDescription = data.customOgDescription || null
    }
    if (data.customOgImage !== undefined) {
      updateData.customOgImage = data.customOgImage || null
    }

    const updatedLink = await db.links.update(data.id, updateData)

    return { link: updatedLink }
  })

// Delete a link
const deleteLinkSchema = z.object({
  id: z.string(),
})

export const deleteLinkFn = createServerFn({ method: 'POST' })
  .inputValidator(deleteLinkSchema)
  .handler(async ({ data }) => {
    const { currentUser } = await authMiddleware()

    if (!currentUser) {
      throw new Error('Unauthorized')
    }

    const link = await db.links.get(data.id)

    if (!link || link.createdBy !== currentUser.$id) {
      throw new Error('Link not found')
    }

    // Delete all associated click events (handle pagination for links with many clicks)
    let hasMore = true
    while (hasMore) {
      const events = await db.clickEvents.list([
        Query.equal('linkId', [data.id]),
        Query.limit(100),
      ])

      if (events.rows.length === 0) {
        hasMore = false
      } else {
        // Delete events in parallel for better performance
        await Promise.all(
          events.rows.map((event) => db.clickEvents.delete(event.$id)),
        )
      }
    }

    await db.links.delete(data.id)

    return { success: true }
  })

// Increment clicks (legacy - kept for compatibility)
const incrementClicksSchema = z.object({
  id: z.string(),
})

export const incrementClicksFn = createServerFn({ method: 'POST' })
  .inputValidator(incrementClicksSchema)
  .handler(async ({ data }) => {
    const link = await db.links.get(data.id)

    if (!link) {
      throw new Error('Link not found')
    }

    await db.links.update(data.id, {
      clicks: link.clicks + 1,
    })

    return { success: true }
  })
